import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const CurrencyContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;

const STORAGE_KEY = 'selora_currency';

/** Currency metadata — order in this object is the order in the selector. */
export const CURRENCIES = {
  EUR: { symbol: '€', flag: '🇪🇺', label: 'Euro' },
  INR: { symbol: '₹', flag: '🇮🇳', label: 'Indian Rupee' },
  USD: { symbol: '$', flag: '🇺🇸', label: 'US Dollar' },
  GBP: { symbol: '£', flag: '🇬🇧', label: 'British Pound' },
  CAD: { symbol: 'C$', flag: '🇨🇦', label: 'Canadian Dollar' },
  AUD: { symbol: 'A$', flag: '🇦🇺', label: 'Australian Dollar' },
};

const FALLBACK_RATES = { EUR: 1, INR: 90, USD: 1.08, GBP: 0.85, CAD: 1.47, AUD: 1.63 };

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    if (typeof window === 'undefined') return 'EUR';
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && CURRENCIES[stored] ? stored : 'EUR';
  });
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [ratesFetchedAt, setRatesFetchedAt] = useState(null);
  const [ratesError, setRatesError] = useState(false);

  const setCurrency = useCallback((c) => {
    if (!CURRENCIES[c]) return;
    setCurrencyState(c);
    try { localStorage.setItem(STORAGE_KEY, c); } catch (_e) { /* noop */ }
  }, []);

  // Fetch live rates on mount + every 60 minutes.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/payments/exchange-rates`);
        if (!res.ok) throw new Error('fetch failed');
        const data = await res.json();
        if (cancelled) return;
        setRates({ ...FALLBACK_RATES, ...(data.rates || {}) });
        setRatesFetchedAt(data.fetched_at || null);
        setRatesError(false);
      } catch (_e) {
        if (cancelled) return;
        setRatesError(true);
      }
    };
    load();
    const id = setInterval(load, 60 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const convert = useCallback((amountEur, target = currency) => {
    const r = rates[target] || 1;
    return amountEur * r;
  }, [rates, currency]);

  const format = useCallback((amountEur, opts = {}) => {
    const target = opts.currency || currency;
    const value = convert(amountEur, target);
    const meta = CURRENCIES[target] || CURRENCIES.EUR;
    const suffix = opts.suffix ? ` ${opts.suffix}` : '';
    // Round to a whole unit for INR/JPY-like currencies, 2 decimals otherwise.
    const decimals = target === 'INR' ? 0 : 2;
    const formatted = value.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return `${meta.symbol}${formatted}${suffix}`;
  }, [convert, currency]);

  const value = useMemo(() => ({
    currency,
    setCurrency,
    rates,
    ratesFetchedAt,
    ratesError,
    convert,
    format,
    meta: CURRENCIES[currency],
    supported: Object.keys(CURRENCIES),
  }), [currency, rates, ratesFetchedAt, ratesError, convert, format, setCurrency]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    return {
      currency: 'EUR',
      setCurrency: () => {},
      rates: FALLBACK_RATES,
      ratesFetchedAt: null,
      ratesError: false,
      convert: (v) => v,
      format: (v) => `€${v.toFixed(2)}`,
      meta: CURRENCIES.EUR,
      supported: Object.keys(CURRENCIES),
    };
  }
  return ctx;
}
